import { getComparator, stableSort } from '@/components/table/functions';
import { formatKey } from '@/utils/constants';
import { clientMatchesDeal, clientMatchesGenericId } from '@/features/clients/helper';
import { getDealClientId } from '@/features/deals/helper';

export const mergeAuditRowsWithEntityNames = ({ rows, clients = [], deals = [] }) =>
	rows.map((row) => {
		const client = clients.find((item) => clientMatchesGenericId(item, row.clientId));
		// Activities store the deal's doc id (`id` on deal rows).
		const deal = deals.find((item) => item.id?.toString() === row.dealId?.toString());

		let clientName = client?.name ?? null;
		const dealName = deal?.dealName ?? null;

		if (deal && !clientName) {
			const dealClient = clients.find((item) => clientMatchesDeal(item, deal));
			clientName = dealClient?.name ?? deal.clientName ?? '';
		}

		return {
			...row,
			...(clientName != null && { clientName }),
			...(dealName != null && { dealName }),
		};
	});

export const enrichAuditRowsForExport = async ({
	rows,
	getBatchClients,
	getBatchDeals,
}) => {
	const rowData = rows.map((row) => ({ ...row }));

	const dealIds = Array.from(new Set(rowData.map((row) => row.dealId).filter(Boolean).map(String)));
	const deals = dealIds.length ? await getBatchDeals(dealIds).unwrap() : [];

	const clientIds = Array.from(
		new Set(
			[...rowData.map((row) => row.clientId), ...deals.map((deal) => getDealClientId(deal))]
				.filter(Boolean)
				.map(String),
		),
	);
	const clients = clientIds.length ? await getBatchClients(clientIds).unwrap() : [];

	return mergeAuditRowsWithEntityNames({ rows: rowData, clients, deals });
};

const getAuditSortValue = (row, field) => {
	if (field === 'performedBy' || field === 'targetUser') {
		return row[field]?.name ?? row[field]?.email ?? '';
	}

	if (field === 'actionType' || field === 'fieldChanged') {
		return row[field] ? formatKey(row[field]) : '';
	}

	if (field === 'clientName') {
		return row.clientName ?? row.clientId ?? '';
	}

	if (field === 'dealName') {
		return row.dealName ?? row.dealId ?? '';
	}

	return row[field] ?? '';
};

export const sortAuditRows = (rows, sortModel) => {
	if (!sortModel?.length) return rows;

	const { field, sort } = sortModel[0];
	const normalized = rows.map((row) => ({
		...row,
		[field]: getAuditSortValue(row, field),
	}));

	return stableSort(normalized, getComparator(sort, field, true));
};
